import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

/**
 * /set-password?token=... — one-time password creation from an admin
 * invitation link. Public route. On success, sends the user to /login.
 */
export default function SetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Missing invitation token");
    if (pw.length < 12) return toast.error("Password must be at least 12 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/set-password", { token, password: pw });
      toast.success("Password set. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(errMsg(err, "The invitation link is invalid or has expired."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--pa-bg,#0a0a0a)]">
      <div className="w-full max-w-md bg-[var(--pa-surface,#111)] border border-[var(--pa-border,#333)] rounded-2xl p-7">
        <div className="flex items-center gap-2 text-[var(--pa-gold,#d4af37)]">
          <ShieldCheck size={16} /> <span className="kicker">Set password</span>
        </div>
        <h1 className="font-serif text-2xl text-[var(--pa-text,#fff)] mt-2">
          Complete your account
        </h1>
        <p className="mt-2 text-xs text-[var(--pa-text-muted,#aaa)] leading-relaxed">
          Choose a password (12+ characters). Two-factor enrollment will be
          required after your first sign-in.
        </p>
        {!token && (
          <div
            data-testid="set-password-no-token"
            className="mt-4 p-3 rounded-md bg-red-500/10 border border-red-500/40 text-xs text-red-200"
          >
            This link is missing an invitation token. Please contact the ProsthoAI administrator.
          </div>
        )}
        <form onSubmit={submit} className="mt-5 space-y-3">
          <label className="block text-xs">
            <span className="kicker block mb-1">New password</span>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                data-testid="set-password-input"
                minLength={12}
                autoComplete="new-password"
                className="w-full bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)] focus:border-[var(--pa-gold,#d4af37)] focus:outline-none rounded p-2.5 pr-10 text-sm text-[var(--pa-text,#fff)]"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                aria-label={show ? "Hide password" : "Show password"}
                data-testid="set-password-toggle"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--pa-text-muted,#aaa)] hover:text-[var(--pa-gold,#d4af37)]"
              >
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>
          <label className="block text-xs">
            <span className="kicker block mb-1">Confirm password</span>
            <input
              type={show ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              data-testid="set-password-confirm"
              minLength={12}
              autoComplete="new-password"
              className="w-full bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)] focus:border-[var(--pa-gold,#d4af37)] focus:outline-none rounded p-2.5 text-sm text-[var(--pa-text,#fff)]"
            />
          </label>
          <button
            type="submit"
            disabled={busy || !token}
            data-testid="set-password-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--pa-gold,#d4af37)] text-[var(--pa-bg,#0a0a0a)] font-semibold rounded-md px-4 py-2.5 hover:opacity-90 transition disabled:opacity-40"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            Set password
          </button>
        </form>
        <p className="mt-4 text-[10px] text-[var(--pa-text-dim,#666)] text-center">
          <Link to="/login" className="hover:text-[var(--pa-gold,#d4af37)]">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
